import React, { ComponentProps } from "react";
import { useFormContext } from "react-hook-form";
import { Form } from "@sscs/ui";
import { getCleanValue } from "Utils";
import CustomEndAdornment from "./CustomEndAdornment";
import styles from "./selectCreateAutocomplete.module.scss";

type SelectCreateAutocompleteProps = ComponentProps<
  typeof Form.Autocomplete
> & {
  isCreatingMode: boolean;
  onModeChange: (isCreatingMode: boolean) => void;
  isFetching?: boolean;
};

const SelectCreateAutocomplete = ({
  isCreatingMode,
  onModeChange,
  ...props
}: SelectCreateAutocompleteProps) => {
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const value: string = getCleanValue(watch(props.name) ?? "");
  const isExistingValue = props.options.includes(value);
  const shouldShowCreateNew =
    value !== "" && !isExistingValue && !isCreatingMode;
  const shouldShowCustomIcon =
    shouldShowCreateNew || (!value && !isCreatingMode);

  const handleInputChange = (
    _e: React.SyntheticEvent<HTMLInputElement, Event>,
    newValue: string,
  ) => {
    setValue(props.name, newValue);
    if (props.options.includes(newValue)) {
      onModeChange(false);
    }
  };

  const autocompleteProps: ComponentProps<typeof Form.Autocomplete> = {
    ...props,
    freeSolo: true,
    onInputChange: handleInputChange,
    errors,
  };

  return !shouldShowCustomIcon ? (
    <Form.Autocomplete {...autocompleteProps} />
  ) : (
    <Form.Autocomplete
      {...autocompleteProps}
      InputProps={{
        className: styles["custom-autocomplete"],
        endAdornment: (
          <CustomEndAdornment
            onCreateNewClick={() => onModeChange(true)}
            shouldShowCreateNew={shouldShowCreateNew}
            subject={props.label?.split(" ")[0] ?? ""}
          />
        ),
      }}
    />
  );
};

export default SelectCreateAutocomplete;
